/**
 * CollocationQuiz — "Which word goes with X?"
 *
 * Cards must carry CollocationExt (`card.collocations: string[]`). The
 * quiz pulls a random correct collocate from `card.collocations` and
 * sources distractors from a pre-computed `distractorIndex` (keyed by
 * headword → "implausible collocate" candidates).
 *
 * Lewis (1993) Lexical Approach + Nation's corpus work both highlight
 * collocations as the highest-leverage differentiator vs. pure
 * vocabulary-list apps — learners who know individual words but not
 * their collocates produce "correct but unnatural" output (*make
 * homework* instead of *do homework*).
 *
 * Per plan §Issue 15: distractor index is pre-computed at content-pipeline
 * time (~5k+ collocations across the deck need an index). PhrasalVerb
 * doesn't need an index because the pool is ~150 items.
 */

import { useState, useCallback, useMemo } from 'preact/hooks';
import type { Card } from '@kevvykevwin/lang-core/types';
import type {
  QuizMode,
  QuizProps,
  QuizOptionState
} from '@kevvykevwin/lang-core/types';
import {
  QuizLayout,
  QuizQuestion,
  QuizOptions,
  QuizFeedback,
  QuizOption,
  useKeyboard,
  QUIZ_CONFIG,
  type SrsSignals
} from '@kevvykevwin/lang-core';

import type { EnglishCardExt, CollocationExt } from '../types.js';

// ============================================
// Types + helpers
// ============================================

/**
 * Distractor index shape: keyed by headword (lowercase), value is an array
 * of words that DON'T collocate with the headword. Pre-computed at
 * content-pipeline time. The runtime quiz mixes the correct collocate
 * with 3 distractors from this list.
 */
export type DistractorIndex = Record<string, string[]>;

function hasCollocations(
  card: Card<EnglishCardExt>
): card is Card<EnglishCardExt> & CollocationExt {
  return Boolean(card.collocations && card.collocations.length > 0);
}

function pickRandom<T>(arr: readonly T[]): T | undefined {
  return arr[Math.floor(Math.random() * arr.length)];
}

function generateOptions(
  correct: string,
  headword: string,
  distractorIndex: DistractorIndex
): string[] {
  const distractorPool = distractorIndex[headword.toLowerCase()] ?? [];
  const distractors = [...distractorPool]
    .filter(d => d !== correct)
    .sort(() => Math.random() - 0.5);

  const options = [correct, ...distractors.slice(0, QUIZ_CONFIG.optionsCount - 1)];

  // Pad with generic semantically-distant words if the index is sparse.
  // These are unlikely to collocate with most things and serve as obvious
  // wrong answers when no better distractor exists.
  const fallback = ['quickly', 'although', 'window', 'orange', 'between'];
  while (options.length < QUIZ_CONFIG.optionsCount) {
    const next = fallback.find(f => !options.includes(f));
    if (!next) break;
    options.push(next);
  }
  return options.sort(() => Math.random() - 0.5);
}

// ============================================
// Inner component
// ============================================

interface InnerProps {
  card: Card<EnglishCardExt> & CollocationExt;
  signals: SrsSignals<EnglishCardExt>;
  distractorIndex: DistractorIndex;
}

function CollocationQuizInner({ card, signals, distractorIndex }: InnerProps) {
  const answered = signals.quizAnswered.value;

  const correct = useMemo(
    () => pickRandom(card.collocations) ?? card.collocations[0],
    [card.collocations]
  );

  const [options] = useState<string[]>(() =>
    generateOptions(correct, card.front, distractorIndex)
  );
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{
    message: string;
    type: 'correct' | 'incorrect';
  } | null>(null);

  const handleAnswer = useCallback(
    (index: number) => {
      if (answered || index >= options.length) return;

      const selected = options[index];
      const isCorrect = selected === correct;

      setSelectedIndex(index);
      signals.quizAnswered.value = true;
      signals.updateWordSRS(card.id, isCorrect);

      if (isCorrect) {
        signals.quizCorrect.value++;
        setFeedback({
          message: `✅ Correct! "${correct} ${card.front}"`,
          type: 'correct'
        });
        signals.showNotification('Correct!', 'success', 1500);
      } else {
        signals.quizIncorrect.value++;
        setFeedback({
          message: `❌ Wrong. Answer: "${correct} ${card.front}"`,
          type: 'incorrect'
        });
        signals.showNotification(
          `Wrong. ${correct} ${card.front}`,
          'error',
          2500
        );
      }

      setTimeout(() => signals.nextCard(), QUIZ_CONFIG.feedbackDelayMs);
    },
    [answered, card, options, signals, correct]
  );

  useKeyboard({
    currentMode: signals.currentMode,
    quizAnswered: signals.quizAnswered,
    flipCard: signals.flipCard,
    nextCard: signals.nextCard,
    prevCard: signals.prevCard,
    onQuizAnswer: handleAnswer
  });

  const getOptionState = (index: number): QuizOptionState => {
    if (!answered) return 'idle';
    const option = options[index];
    if (option === correct) return 'correct';
    if (index === selectedIndex) return 'incorrect';
    return 'disabled';
  };

  return (
    <QuizLayout modeName="Collocation">
      <QuizQuestion label={`Which word goes with "${card.front}"?`}>
        <div className="text-3xl font-bold text-gray-800 mb-3">
          ____ <span className="text-primary-600">{card.front}</span>
        </div>
        <div className="text-sm text-slate-500 italic">{card.back}</div>
      </QuizQuestion>

      <QuizOptions>
        {options.map((option, index) => (
          <QuizOption
            key={`${option}-${index}`}
            text={`${option} ${card.front}`}
            state={getOptionState(index)}
            onClick={() => handleAnswer(index)}
            disabled={answered}
            index={index}
          />
        ))}
      </QuizOptions>

      {feedback && <QuizFeedback message={feedback.message} type={feedback.type} />}
    </QuizLayout>
  );
}

// ============================================
// Factory
// ============================================

export interface CreateCollocationQuizOptions {
  signals: SrsSignals<EnglishCardExt>;
  /**
   * Pre-computed distractor index. Loaded at boot from
   * src/data/distractorIndex.json (built by the content pipeline).
   * Empty {} is allowed — the component falls back to a small pool
   * of semantically-distant words.
   */
  distractorIndex: DistractorIndex;
}

export function createCollocationQuiz(
  options: CreateCollocationQuizOptions
): QuizMode<EnglishCardExt> {
  const { signals, distractorIndex } = options;
  return {
    id: 'collocation-quiz',
    label: 'Collocations',
    icon: '🔗',
    isAvailable: deck => deck.cards.some(c => hasCollocations(c)),
    component: ({ card }: QuizProps<EnglishCardExt>) => {
      if (!hasCollocations(card)) {
        return (
          <div className="card text-center py-12 text-slate-500">
            This card has no collocation data. Switch decks or pick another mode.
          </div>
        );
      }
      return (
        <CollocationQuizInner
          card={card}
          signals={signals}
          distractorIndex={distractorIndex}
        />
      );
    }
  };
}
